import { useState, useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { formatTimeLeft, secondsLeft, type ProgressSample } from "@/lib/opTiming";

export type OperationStatus = "running" | "completed" | "error" | "cancelled";

interface OperationPillProps {
  icon: ReactNode;
  label: string;
  color: string;
  completed: number;
  total: number;
  isExpanded: boolean;
  status?: OperationStatus;
  /** Override the count display (e.g., "75%" instead of "75/100"). */
  countLabel?: string;
  /** No count to show progress against (e.g. a Batch API job): the bar shimmers
   *  and no time-left is computed. */
  uncounted?: boolean;
  /** Job start (ms epoch) when known server-side, so elapsed survives a reload. */
  startedAt?: number | null;
  /** "usually 4–15 min" from past runs; shown while running when no time-left. */
  usual?: string | null;
  /** Pace from this point (e.g. the server's job start at 0 done) instead of
   *  the first progress this pill saw — a reloaded pill knows its real pace. */
  paceFrom?: ProgressSample | null;
  /** End-state line: the error reason, or what a cancel left behind. */
  message?: string | null;
  /** Inline action on an ended pill (e.g. Retry). */
  action?: { label: string; onClick: () => void };
  /** Shown as ✕ on ended pills. */
  onDismiss?: () => void;
  onClick: () => void;
}

export function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds >= 3600) {
    // Batch jobs can run for hours; "564:29" reads as nonsense.
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h} h ${m} min`;
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const END_COLOR: Record<Exclude<OperationStatus, "running">, string> = {
  completed: "#a6e3a1",
  error: "#f38ba8",
  cancelled: "#6c7086",
};

export function OperationPill({
  icon,
  label,
  color,
  completed,
  total,
  isExpanded,
  status = "running",
  countLabel,
  uncounted = false,
  startedAt,
  usual,
  paceFrom,
  message,
  action,
  onDismiss,
  onClick,
}: OperationPillProps) {
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isRunning = status === "running";
  const indeterminate = isRunning && (uncounted || percent === 0);
  const accent = isRunning ? color : END_COLOR[status];

  // Elapsed timer: from the server's start time when known, else from when
  // this run began on screen. A pill kept across runs (an undismissed
  // cancelled pill, then a new run) restarts both clock and pace.
  const [now, setNow] = useState(() => Date.now());
  const [runStart, setRunStart] = useState(() => Date.now());
  const firstSample = useRef<ProgressSample | null>(null);
  const wasRunning = useRef(isRunning);
  if (isRunning !== wasRunning.current) {
    wasRunning.current = isRunning;
    firstSample.current = null;
    if (isRunning) {
      const t = Date.now();
      setRunStart(t);
      setNow(t);
    }
  }
  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isRunning]);
  const elapsed = Math.max(0, Math.round((now - (startedAt ?? runStart)) / 1000));

  // Pace for time-left: first progress this run showed → latest.
  if (isRunning && !uncounted && firstSample.current == null) {
    firstSample.current = paceFrom ?? { t: Date.now(), done: completed };
  }
  const left =
    isRunning && !uncounted && firstSample.current
      ? secondsLeft(firstSample.current, { t: now, done: completed }, total)
      : null;
  const timeText = !isRunning
    ? null
    : left != null
      ? `${formatElapsed(elapsed)} · ${formatTimeLeft(left)}`
      : usual
        ? `${formatElapsed(elapsed)} · ${usual}`
        : formatElapsed(elapsed);

  return (
    <div
      data-op-pill={status}
      className={`flex items-center gap-1.5 rounded border bg-slate-900 text-sm transition-all
        ${isExpanded ? "ring-1 ring-white/20" : ""}`}
      style={{ borderColor: accent }}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-1.5 rounded py-1.5 pl-2.5 pr-1 hover:brightness-110"
        aria-label={uncounted ? `${label}: ${countLabel || "running"}` : `${label}: ${completed} of ${total}`}
        aria-expanded={isRunning ? isExpanded : undefined}
      >
        <span style={{ color: accent }}>
          {status === "completed" ? "✓" : status === "error" ? "!" : status === "cancelled" ? "■" : icon}
        </span>
        <span className="font-medium text-slate-200 whitespace-nowrap">{label}</span>
        {isRunning && (
          <div
            className="h-1.5 w-16 rounded-full bg-slate-800 overflow-hidden"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {indeterminate ? (
              <div
                className="h-full w-1/3 rounded-full animate-pulse"
                style={{ backgroundColor: color, animation: "shimmer 1.5s ease-in-out infinite" }}
              />
            ) : (
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${percent}%`, backgroundColor: color }}
              />
            )}
          </div>
        )}
        {(isRunning || status === "completed") && (
          <span className="text-slate-400 tabular-nums whitespace-nowrap">
            {countLabel ?? `${completed}/${total}`}
          </span>
        )}
        {timeText && (
          <span className="text-slate-400 tabular-nums whitespace-nowrap">{timeText}</span>
        )}
        {!isRunning && message && (
          <span className="text-slate-400 whitespace-nowrap">{message}</span>
        )}
      </button>
      {!isRunning && action && (
        <button
          type="button"
          onClick={action.onClick}
          className="rounded px-1 text-xs font-medium text-blue-400 hover:text-blue-300"
        >
          {action.label}
        </button>
      )}
      {!isRunning && onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`Dismiss ${label}`}
          className="mr-1.5 rounded p-0.5 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
        >
          <X className="size-3" />
        </button>
      ) : (
        <span className="pr-1.5" />
      )}
    </div>
  );
}
